import { Card, Dialog, Icon, Portal, Stack, Text } from '@chakra-ui/react';
import {
  useLazyGetAllProjectStatesQuery,
  useRemoveProjectStateMutation,
  useSaveProjectStateMutation,
  useSetActiveProjectStateMutation,
} from '../lib/api/GraphAPI';
import { toaster } from './ui/toaster';
import { useEffect, useState } from 'react';
import { HelpCircle } from 'react-feather';
import { setProjectState } from '../lib/state/slices/ProjectStateSlice';
import { useAppDispatch, useAppSelector } from '../lib/hooks';
import ProjectState from '../lib/state/ViewTypes';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from './ui/tooltip';
import { format, parse } from 'date-fns';
import StyledButton from './ui/styledButton';

export const ProjectsOverviewModal = (props: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [
    getAllProjects,
    {
      data: getAllProjectsData,
      isSuccess: getAllProjectsIsSuccess,
      isError: getAllProjectsIsError,
      error: getAllProjectsError,
    },
  ] = useLazyGetAllProjectStatesQuery();

  const [
    saveProjectState,
    {
      // data: saveProjectData,
      // isSuccess: saveProjectIsSuccess,
      isError: saveProjectIsError,
      error: saveProjectError,
    },
  ] = useSaveProjectStateMutation();

  const [
    removeProjectState,
    {
      // data: removeProjectData,
      // isSuccess: removeProjectIsSuccess,
      isError: removeProjectIsError,
      error: removeProjectError,
    },
  ] = useRemoveProjectStateMutation();

  const [
    setActive,
    {
      // data: setActiveData,
      // isSuccess: setActiveIsSuccess,
      isError: setActiveIsError,
      error: setActiveError,
    },
  ] = useSetActiveProjectStateMutation();

  const [selectedProjectState, setSelectedProjectState] = useState<number | null>(null);

  const state = useAppSelector((state) => state.viewReducer);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleClose = () => {
    setSelectedProjectState(null);
    props.setOpen(false);
  };

  const handleSubmit = () => {
    if (
      getAllProjectsIsSuccess &&
      getAllProjectsData &&
      getAllProjectsData.length > 0 &&
      selectedProjectState !== null
    ) {
      if (state.isAutosaveActive) {
        console.log('autosaved via Modal');
        saveProjectState(state);
      }

      const newProjectState: ProjectState = getAllProjectsData[selectedProjectState].json;

      navigate('/research');

      dispatch(setProjectState(newProjectState));
      setActive(newProjectState.id);

      handleClose();
    }
  };

  const handleDelete = (projectStateId: string) => {
    if (projectStateId !== '') {
      console.log('Deleting project...');
      removeProjectState(projectStateId);
      getAllProjects();
    }
  };

  useEffect(() => {
    if (props.open) {
      getAllProjects();
    }
  }, [props.open]);

  useEffect(() => {
    if (getAllProjectsIsError) {
      toaster.create({
        title: 'Error while fetching projects',
        description: `Something went wrong while retrieving all of your project: ${getAllProjectsError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [getAllProjectsIsError]);

  useEffect(() => {
    if (saveProjectIsError) {
      toaster.create({
        title: 'Error while saving project',
        description: `Something went wrong while saving the current project: ${saveProjectError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [saveProjectIsError]);

  useEffect(() => {
    if (removeProjectIsError) {
      toaster.create({
        title: 'Error while removing project',
        description: `Something went wrong while attempting to delete a project: ${removeProjectError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [removeProjectIsError]);

  useEffect(() => {
    if (setActiveIsError) {
      toaster.create({
        title: 'Error while switching projects',
        description: `Something went wrong while switching the active project: ${setActiveError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [setActiveIsError]);

  return (
    <Dialog.Root lazyMount open={props.open} onOpenChange={(e) => props.setOpen(e.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content backgroundColor="midCardBG">
            <Dialog.Header>
              <Dialog.Title>
                <Stack direction="row" alignItems="center">
                  <Text color="primaryText">Select a project</Text>
                  <Tooltip
                    content="A project contains your graphs, folders and search information"
                    placement="right"
                  >
                    <Icon color="secondaryText">
                      <HelpCircle width={20} height={20} />
                    </Icon>
                  </Tooltip>
                </Stack>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack>
                {getAllProjectsIsSuccess && getAllProjectsData && getAllProjectsIsSuccess ? (
                  [...getAllProjectsData].map((e, i) => {
                    const isSelected = i === selectedProjectState;
                    const isCurrent = e.json.id === state.id;
                    const dateTimeObj = parse(
                      e.last_modified,
                      "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                      new Date(),
                    );
                    const dateTime = format(dateTimeObj, 'dd. MMM yyyy HH:mm');

                    return (
                      <Card.Root
                        key={'card_' + e.json.id}
                        variant="subtle"
                        _hover={{
                          bg: 'cardHover',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        background={isSelected ? 'cardActive' : 'cardDefault'}
                      >
                        <Card.Body
                          key={'card_body_' + e.json.id}
                          onClick={() => setSelectedProjectState(i)}
                        >
                          <Stack
                            key={'card_stack_top_' + e.json.id}
                            width="100%"
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Text
                              color="secondaryText"
                              textOverflow="clip"
                              overflowX="hidden"
                              key={'card_text_name_' + e.json.id}
                              fontWeight={isCurrent ? 'bold' : ''}
                            >
                              {e.json.name} {isCurrent ? '(Current)' : ''}
                            </Text>
                          </Stack>
                        </Card.Body>
                      </Card.Root>
                    );
                  })
                ) : (
                  <Text>No projects found</Text>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <StyledButton variant="outline" text="Cancel" onClick={handleClose} />
              <StyledButton variant="solid" text="Open" onClick={handleSubmit} />
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
